/**
 * Metafans BuddyPress Reactions
 * Handles Likes, Emoji Reactions, and Reaction Viewers.
 */

import { postAction } from './api.js';

class ReactionsHandler {
    constructor() {
        this.init();
    }

    init() {
        this.bindEvents();
    }

    bindEvents() {
        const doc = document;

        // 1. Generic Like/Reaction Button Click
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.th-bp-post-like-button a');
            if (btn) {
                e.preventDefault();
                this.handleLikeClick(btn);
            }
        });

        // 2. Specific Reaction Icon Click (Dropdown)
        doc.addEventListener('click', (e) => {
            if (e.target.matches('.th-bp-post-like-button .reaction_icons img')) {
                e.preventDefault();
                e.stopPropagation(); // Prevent bubbling to the main link
                this.handleReactionIconClick(e.target);
            }
        });

        // 3. View All Reactions (Modal)
        doc.addEventListener('click', (e) => {
            const btn = e.target.closest('.reactions-meta');
            if (btn) {
                e.preventDefault();
                this.openReactionViewer(btn.dataset.activityId);
            }
        });

        // 4. Reaction Viewer Tabs
        doc.addEventListener('click', (e) => {
            const tab = e.target.closest('.th-activity-reaction-viewer .reaction_tabs li a');
            if (tab) {
                e.preventDefault();
                this.switchReactionTab(tab);
            }
        });

        // 5. Close Viewer
        doc.addEventListener('click', (e) => {
            if (e.target.closest('.th-activity-reaction-viewer .close')) {
                document.querySelector('.th-activity-reaction-viewer').classList.remove('show');
            }
        });
    }

    /**
     * Helper to select buttons safely
     * FIX: Prevents Parent likes from bleeding into Replies
     */
    getButtonsSelector(activityId, commentId, replyIndex) {
        if (commentId) {
            let selector = `.th-bp-post-like-button a[data-comment-id="${commentId}"]`;

            // If this is a specific reply
            if (replyIndex !== undefined && replyIndex !== null && replyIndex !== '') {
                selector += `[data-reply-index="${replyIndex}"]`;
            } else {
                // CRITICAL FIX: If this is a parent comment, EXCLUDE elements that have a reply index
                // This stops the selector from grabbing all the children replies
                selector += `:not([data-reply-index])`;
            }

            return document.querySelectorAll(selector);
        } else {
            // Activity Like Button
            return document.querySelectorAll(`.th-bp-post-like-button a[data-id="${activityId}"]:not([data-comment-id])`);
        }
    }

    /**
     * Handles the main Like/Love button toggle
     */
    async handleLikeClick(btn) {
        const currentReaction = btn.dataset.reaction;
        const activityId = btn.dataset.id;
        const commentId = btn.dataset.commentId;
        const replyIndex = btn.dataset.replyIndex;

        // Optimistic UI Update
        const isRemoving = currentReaction !== '';
        const newReaction = isRemoving ? '' : (Metafans_JS.is_modern_reaction ? 'love' : 'like');

        // Parse current count
        let countMatch = btn.innerText.match(/\d+/);
        let count = countMatch ? parseInt(countMatch[0]) : 0;
        count = isRemoving ? Math.max(0, count - 1) : count + 1;

        // Render Button HTML
        let html;
        if (Metafans_JS.is_modern_reaction) {
            const icon = newReaction ? Metafans_JS.love_svg_fill : Metafans_JS.love_svg_solid;
            html = `${icon} ${count > 0 ? count : ''}`;
        } else {
            // Legacy
            if (newReaction) {
                html = `<img src="${Metafans_JS.like_img}" /> ${Metafans_JS.like_text} ${count > 1 ? count : ''}`;
            } else {
                html = `${Metafans_JS.like_base_img} ${Metafans_JS.like_base_text} ${count > 0 ? count : ''}`;
            }
        }

        // --- Use the Fixed Selector ---
        const allButtons = this.getButtonsSelector(activityId, commentId, replyIndex);

        allButtons.forEach(b => {
            b.dataset.reaction = newReaction;
            b.innerHTML = html;
        });

        // API Call
        const action = newReaction === '' ? 'decrement' : newReaction;

        if (commentId) {
            this.postCommentReaction(action, activityId, commentId, replyIndex);
        } else {
            this.postActivityReaction(action, activityId);
        }
    }

    /**
     * Handles clicking a specific emoji (Haha, Wow, etc.)
     */
    handleReactionIconClick(img) {
        if (!Metafans_JS.logged_in) return;

        const container = img.closest('.th-bp-post-like-button');
        const clickedBtn = container.querySelector('a');

        const reactionType = img.dataset.type;
        const activityId = img.dataset.activityId;
        const commentId = clickedBtn.dataset.commentId;
        const replyIndex = clickedBtn.dataset.replyIndex;
        const reactionData = this.getReactionData(reactionType);

        if (!reactionData) return;

        // --- Use the Fixed Selector ---
        const allButtons = this.getButtonsSelector(activityId, commentId, replyIndex);

        allButtons.forEach(b => {
            b.dataset.reaction = reactionType;
            b.innerHTML = `<img src="${reactionData.img}" /> ${reactionData.text}`;

            const parentContainer = b.closest('.th-bp-post-like-button');
            const iconsContainer = parentContainer ? parentContainer.querySelector('.reaction_icons') : null;
            if (iconsContainer) {
                iconsContainer.style.opacity = '0';
                setTimeout(() => iconsContainer.style.opacity = '1', 500);
            }
        });

        // API Call
        if (commentId) {
            this.postCommentReaction(reactionType, activityId, commentId, replyIndex);
        } else {
            this.postActivityReaction(reactionType, activityId);
        }
    }

    async postActivityReaction(type, activityId) {
        try {
            const response = await postAction('th_bp_activity_reaction', {
                reaction_type: type,
                activity_id: activityId
            });

            if (!Metafans_JS.is_modern_reaction) {
                const feedMeta = document.querySelector(`#activity-${activityId} .reactions-meta`);
                if (feedMeta) feedMeta.innerHTML = response;

                const popup = document.querySelector('.th-media-viewer-container.show');
                if (popup && popup.dataset.activityId === activityId) {
                    const popupMeta = popup.querySelector('.reactions-meta');
                    if (popupMeta) popupMeta.innerHTML = response;
                }
            }
        } catch (err) {
            console.error('Reaction failed', err);
        }
    }

    async postCommentReaction(type, activityId, commentId, replyIndex) {
        try {
            const activityEl = document.getElementById(`activity-${activityId}`);
            const cur_num = activityEl ? activityEl.querySelectorAll('.activity-comments > ul > li').length : 4;

            const response = await postAction('comment_react', {
                reaction_type: type,
                comment_id: commentId,
                activity_id: activityId,
                reply_index: replyIndex,
                cur_num_of_open_cmnt: cur_num
            });

            if (response && response.data && response.data.html) {
                const feedComments = document.querySelector(`#activity-${activityId} .activity-comments`);
                if (feedComments) feedComments.innerHTML = response.data.html;

                const popupComments = document.querySelector('.th-media-viewer-container.show .comment_section');
                if (popupComments) popupComments.innerHTML = response.data.html;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async openReactionViewer(activityId) {
        const viewer = document.querySelector('.th-activity-reaction-viewer');
        if (!viewer) return;

        viewer.classList.add('show');
        const container = viewer.querySelector('.reactions');
        container.innerHTML = '<div class="loading-spinner"></div>';

        try {
            const html = await postAction('th_bp_activity_all_reaction', { activity_id: activityId });
            container.innerHTML = html;
        } catch (err) {
            container.innerHTML = 'Error loading reactions.';
        }
    }

    switchReactionTab(tab) {
        const viewer = tab.closest('.th-activity-reaction-viewer');
        const targetId = tab.getAttribute('href');

        viewer.querySelectorAll('.reaction_tabs li a').forEach(a => a.classList.remove('active'));
        tab.classList.add('active');

        viewer.querySelectorAll('.single-reactions').forEach(el => el.style.display = 'none');
        const target = viewer.querySelector(`.single-reactions${targetId}`);
        if (target) target.style.display = 'block';
    }

    getReactionData(type) {
        const map = {
            haha: { img: Metafans_JS.haha_img, text: Metafans_JS.haha_text },
            wow: { img: Metafans_JS.wow_img, text: Metafans_JS.wow_text },
            angry: { img: Metafans_JS.angry_img, text: Metafans_JS.angry_text },
            sad: { img: Metafans_JS.sad_img, text: Metafans_JS.sad_text },
            love: { img: Metafans_JS.love_img, text: Metafans_JS.love_text },
            like: { img: Metafans_JS.like_img, text: Metafans_JS.like_text },
        };
        return map[type];
    }
}

export function initReactions() {
    return new ReactionsHandler();
}